import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  CircularProgress,
  Pagination,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { auditApi } from '../../api';
import type { AuditLog } from '../../types';
import { AUDIT_ACTION_LABELS, AUDIT_CATEGORY_LABELS } from '../../types';

// Grouped audit log entry type
interface GroupedAuditLog {
  logs: AuditLog[];
  count: number;
}

export interface AuditPanelProps {
  boardId: string;
}

// Group actions by category for the dropdown
const ACTION_CATEGORIES: Record<string, string[]> = {
  board: ['board_completed', 'board_created', 'board_locked', 'board_published', 'board_reset', 'board_unlocked', 'board_unpublished', 'board_updated', 'description_updated', 'numbers_randomized', 'payout_config_updated', 'team_name_updated'],
  players: ['manager_added', 'merge_request_approved', 'merge_request_cancelled', 'merge_request_created', 'merge_request_denied', 'offline_player_merged', 'player_invited', 'player_joined', 'players_randomized', 'square_request_approved', 'square_request_denied', 'square_requested'],
  squares: ['square_assigned', 'square_claimed', 'square_cleared'],
  hits: ['score_deleted', 'score_recorded', 'score_updated'],
  payments: ['payment_recorded', 'payment_updated', 'payout_issued', 'winnings_paid_out'],
};

const getCategoryColor = (category: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'default' => {
  switch (category) {
    case 'board': return 'primary';
    case 'players': return 'secondary';
    case 'squares': return 'success';
    case 'hits': return 'warning';
    case 'payments': return 'info';
    default: return 'default';
  }
};

// Memoized audit log entry
interface AuditLogEntryProps {
  group: GroupedAuditLog;
}

const AuditLogEntry = React.memo<AuditLogEntryProps>(({ group }) => {
  const log = group.logs[0]; // Use the first (most recent) log for display
  const count = group.count;

  return (
    <Paper sx={{ p: 2, position: 'relative' }}>
      {count > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 8,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            fontSize: '0.75rem',
            fontWeight: 'bold',
            lineHeight: 1.2,
          }}
        >
          ×{count}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, pr: count > 1 ? 4 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              label={AUDIT_CATEGORY_LABELS[log.category] || log.category}
              color={getCategoryColor(log.category)}
              variant="outlined"
            />
            <Chip
              size="small"
              label={AUDIT_ACTION_LABELS[log.action] || log.action}
            />
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {log.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {log.user?.displayName || 'Unknown'} - {new Date(log.createdAt).toLocaleString()}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
});

AuditLogEntry.displayName = 'AuditLogEntry';

export const AuditPanel: React.FC<AuditPanelProps> = ({ boardId }) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);

  const limit = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await auditApi.getLogs(boardId, {
        category: categoryFilter || undefined,
        action: actionFilter || undefined,
        limit,
        offset: (page - 1) * limit,
      });
      setLogs(response.logs || []);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [boardId, categoryFilter, actionFilter, page]);

  const fetchFilters = useCallback(async () => {
    try {
      const filters = await auditApi.getFilters(boardId);
      setCategories(filters.categories);
      setActions(filters.actions);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  }, [boardId]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.ceil(total / limit);

  // Group consecutive logs by action and user
  const groupedLogs = useMemo((): GroupedAuditLog[] => {
    if (logs.length === 0) return [];

    const groups: GroupedAuditLog[] = [];
    let currentGroup: GroupedAuditLog | null = null;

    for (const log of logs) {
      // Check if this log can be grouped with the current group
      // Group by: same action, same user
      if (
        currentGroup &&
        currentGroup.logs[0].action === log.action &&
        currentGroup.logs[0].userId === log.userId
      ) {
        currentGroup.logs.push(log);
        currentGroup.count++;
      } else {
        // Start a new group
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = { logs: [log], count: 1 };
      }
    }

    // Don't forget the last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  }, [logs]);

  return (
    <Box>
      {/* Explanation box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Track all changes made to your board including settings updates, player activity, square claims, scores, and payments. Use filters to focus on specific types of activity.
          {' '}
          <Link component={RouterLink} to="/support/boards#audit-log">
            Learn more →
          </Link>
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Audit Log</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {AUDIT_CATEGORY_LABELS[cat] || cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={actionFilter}
              label="Action"
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {Object.entries(ACTION_CATEGORIES).map(([category, categoryActions]) => {
                const availableActions = categoryActions.filter(a => actions.includes(a)).sort();
                if (availableActions.length === 0) return null;
                return [
                  <ListSubheader key={`header-${category}`} sx={{ bgcolor: 'grey.100', fontWeight: 'bold' }}>
                    {AUDIT_CATEGORY_LABELS[category] || category}
                  </ListSubheader>,
                  ...availableActions.map((action) => (
                    <MenuItem key={action} value={action} sx={{ pl: 3 }}>
                      {(AUDIT_ACTION_LABELS[action] || action).replace(/^(Board|Player|Square|Score|Payment)\s+/i, '')}
                    </MenuItem>
                  ))
                ];
              })}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : groupedLogs.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No audit logs found
          </Typography>
        </Paper>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {groupedLogs.map((group) => (
              <AuditLogEntry key={group.logs[0].id} group={group} />
            ))}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            Showing {logs.length} of {total} entries
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuditPanel;
